import React, { useEffect, FC } from "react";

import { useAuth } from "Components/Authentication/AuthProvider";
import { isCognitoConfigured } from "Utils/authOidcClient";
import { getCognitoRetryCount } from "Utils/cognito";

import {
  SignInFormContainerStyled,
  TitleStyled,
  LoginErrMsgStyled,
  ForgotPasswordStyled,
  CognitoLoginButton,
} from "./styles";

type LoginProps = {
  onForgotSelected: () => void;
};

/**
 * The only human login is the shared provider's hosted-UI redirect (DEV-6053); the password
 * form and its Redux thunk are gone, so a deployment without Cognito has nothing to sign in with.
 */
export const LoginComponent: FC<LoginProps> = ({ onForgotSelected }) => {
  const { login, error } = useAuth();

  // The shared provider builds the authorization request (Discovery + PKCE) and redirects.
  // The landing path is explicit: `login()` would otherwise return to `/login` itself.
  const handleCognitoLogin = () => {
    void login("/");
  };

  const cognitoConfigured = isCognitoConfigured();

  // Stale-session auto-retry: if CognitoCallback failed and redirected us
  // through /logout (counter incremented), kick off SSO again immediately
  // so the user sees a single seamless redirect instead of the login screen.
  useEffect(() => {
    if (cognitoConfigured && getCognitoRetryCount() > 0) {
      console.info(
        "[Cognito] Stale-session recovery: re-initiating SSO after /logout"
      );
      void login("/");
    }
  }, []);

  return (
    <SignInFormContainerStyled onSubmit={(event) => event.preventDefault()}>
      <TitleStyled>Sign In</TitleStyled>
      {cognitoConfigured ? (
        <CognitoLoginButton type="button" onClick={handleCognitoLogin}>
          Sign in with SSO
        </CognitoLoginButton>
      ) : (
        <LoginErrMsgStyled>
          Single sign-on is not configured for this deployment.
        </LoginErrMsgStyled>
      )}
      <ForgotPasswordStyled onClick={onForgotSelected}>
        FORGOT PASSWORD
      </ForgotPasswordStyled>
      {error && <LoginErrMsgStyled>{error.message}</LoginErrMsgStyled>}
    </SignInFormContainerStyled>
  );
};
